import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Box,
  Tooltip,
  Tab,
  Tabs
} from "@material-ui/core";
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Assessment as ReportIcon,
  CloudUpload as CloudUploadIcon,
  PersonAdd as PersonAddIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  campaignCard: {
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  statusChip: {
    fontWeight: "bold",
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
  charCounter: {
    textAlign: "right",
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  segmentInfo: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
  },
}));

const SmsExpress = () => {
  const classes = useStyles();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignReport, setCampaignReport] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Form state
  const [campaignName, setCampaignName] = useState("");
  const [message, setMessage] = useState("");
  
  // Recipients state
  const [recipients, setRecipients] = useState([]);
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [csvFile, setCsvFile] = useState(null);

  useEffect(() => {
    loadCampaigns();
    const interval = setInterval(loadCampaigns, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/sms-campaigns");
      setCampaigns(data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const calculateSegments = (text) => {
    if (!text) return 0;
    const length = text.length;
    if (length <= 160) return 1;
    return Math.ceil(length / 153);
  };

  const handleCreateCampaign = async () => {
    try {
      if (!campaignName) {
        toast.error("Por favor ingresa el nombre de la campaña");
        return;
      }

      if (!message) {
        toast.error("Por favor ingresa el mensaje");
        return;
      }

      if (message.length > 1600) {
        toast.error("El mensaje no puede exceder 1600 caracteres");
        return;
      }

      setLoading(true);

      const { data } = await api.post("/sms-campaigns", {
        name: campaignName,
        message: message
      });

      toast.success("Campaña creada exitosamente");
      setCreateDialogOpen(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast.error(error.response?.data?.error || "Error al crear la campaña");
    } finally {
      setLoading(false);
    }
  };

  const handleLaunchCampaign = async (campaignId) => {
    if (!window.confirm("¿Estás seguro de iniciar esta campaña de SMS?")) {
      return;
    }

    try {
      await api.post(`/sms-campaigns/${campaignId}/launch`);
      toast.success("Campaña iniciada");
      loadCampaigns();
    } catch (error) {
      console.error("Error launching campaign:", error);
      toast.error(error.response?.data?.error || "Error al iniciar la campaña");
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm("¿Estás seguro de cancelar esta campaña?")) {
      return;
    }

    try {
      await api.post(`/sms-campaigns/${campaignId}/cancel`);
      toast.success("Campaña cancelada");
      loadCampaigns();
    } catch (error) {
      console.error("Error canceling campaign:", error);
      toast.error("Error al cancelar la campaña");
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta campaña?")) {
      return;
    }

    try {
      await api.delete(`/sms-campaigns/${campaignId}`);
      toast.success("Campaña eliminada");
      loadCampaigns();
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Error al eliminar la campaña");
    }
  };

  const handleViewDetails = async (campaign) => {
    try {
      const { data } = await api.get(`/sms-campaigns/${campaign.id}`);
      setSelectedCampaign(data);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error loading campaign details:", error);
      toast.error("Error al cargar los detalles");
    }
  };

  const handleViewReport = async (campaign) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/sms-campaigns/${campaign.id}/stats`);
      setCampaignReport(data);
      setReportDialogOpen(true);
    } catch (error) {
      console.error("Error loading campaign report:", error);
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const handleManageRecipients = async (campaign) => {
    try {
      setSelectedCampaign(campaign);
      const { data } = await api.get(`/sms-campaigns/${campaign.id}/recipients`);
      setRecipients(data);
      setRecipientsDialogOpen(true);
    } catch (error) {
      console.error("Error loading recipients:", error);
      toast.error("Error al cargar destinatarios");
    }
  };

  const handleAddRecipient = async () => {
    try {
      if (!recipientPhone) {
        toast.error("Por favor ingresa un número de teléfono");
        return;
      }

      await api.post(`/sms-campaigns/${selectedCampaign.id}/recipients`, {
        phoneNumber: recipientPhone,
        name: recipientName
      });

      toast.success("Destinatario agregado");
      
      // Reload recipients
      const { data } = await api.get(`/sms-campaigns/${selectedCampaign.id}/recipients`);
      setRecipients(data);
      
      setRecipientPhone("");
      setRecipientName("");
    } catch (error) {
      console.error("Error adding recipient:", error);
      toast.error(error.response?.data?.error || "Error al agregar destinatario");
    }
  };

  const handleImportCSV = async () => {
    try {
      if (!csvFile) {
        toast.error("Por favor selecciona un archivo CSV");
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvContent = e.target.result;
          
          const { data } = await api.post(
            `/sms-campaigns/${selectedCampaign.id}/recipients/import`,
            { csvContent }
          );

          toast.success(`${data.imported} destinatarios importados`);
          
          if (data.errors && data.errors.length > 0) {
            toast.warning(`${data.errors.length} errores encontrados`);
          }

          // Reload recipients
          const recipientsData = await api.get(`/sms-campaigns/${selectedCampaign.id}/recipients`);
          setRecipients(recipientsData.data);
          
          setCsvFile(null);
        } catch (error) {
          console.error("Error importing CSV:", error);
          toast.error("Error al importar CSV");
        }
      };
      
      reader.readAsText(csvFile);
    } catch (error) {
      console.error("Error reading CSV:", error);
      toast.error("Error al leer el archivo CSV");
    }
  };

  const handleRemoveRecipient = async (recipientId) => {
    try {
      await api.delete(`/sms-campaigns/${selectedCampaign.id}/recipients/${recipientId}`);
      toast.success("Destinatario eliminado");
      
      // Reload recipients
      const { data } = await api.get(`/sms-campaigns/${selectedCampaign.id}/recipients`);
      setRecipients(data);
    } catch (error) {
      console.error("Error removing recipient:", error);
      toast.error("Error al eliminar destinatario");
    }
  };

  const resetForm = () => {
    setCampaignName("");
    setMessage("");
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "default",
      sending: "primary",
      completed: "default",
      failed: "default",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pendiente",
      sending: "Enviando",
      completed: "Completada",
      failed: "Fallida",
      cancelled: "Cancelada",
    };
    return labels[status] || status;
  };

  const calculateProgress = (campaign) => {
    if (!campaign.totalRecipients) return 0;
    const completed = campaign.sentCount + campaign.failedCount;
    return (completed / campaign.totalRecipients) * 100;
  };

  const insertVariable = (variable) => {
    setMessage(message + ` {${variable}}`);
  };

  const segments = calculateSegments(message);
  const charCount = message.length;

  return (
    <MainContainer>
      <MainHeader>
        <Title>SMS Express</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            Nueva Campaña SMS
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2}>
          {campaigns.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="body1" align="center" color="textSecondary">
                    No hay campañas de SMS creadas. Crea tu primera campaña para comenzar.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            campaigns.map((campaign) => (
              <Grid item xs={12} key={campaign.id}>
                <Card className={classes.campaignCard}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6">{campaign.name}</Typography>
                        <Chip
                          label={getStatusLabel(campaign.status)}
                          color={getStatusColor(campaign.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">
                          Total: {campaign.totalRecipients} destinatarios
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Enviados: {campaign.sentCount} | Fallidos: {campaign.failedCount}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={calculateProgress(campaign)}
                          className={classes.progressBar}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          {campaign.status === "pending" && (
                            <>
                              <Tooltip title="Gestionar Destinatarios">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleManageRecipients(campaign)}
                                >
                                  <PersonAddIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Iniciar">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleLaunchCampaign(campaign.id)}
                                >
                                  <PlayIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {campaign.status === "sending" && (
                            <Tooltip title="Cancelar">
                              <IconButton
                                onClick={() => handleCancelCampaign(campaign.id)}
                              >
                                <StopIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ver Detalles">
                            <IconButton
                              onClick={() => handleViewDetails(campaign)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver Reporte">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewReport(campaign)}
                            >
                              <ReportIcon />
                            </IconButton>
                          </Tooltip>
                          {campaign.status === "pending" && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDeleteCampaign(campaign.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Paper>

      {/* Create Campaign Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Nueva Campaña de SMS</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            label="Nombre de la Campaña"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            margin="normal"
          />

          <Box mt={2} mb={1}>
            <Typography variant="subtitle2" gutterBottom>
              Variables disponibles:
            </Typography>
            <Button size="small" onClick={() => insertVariable("nombre")}>
              {"{nombre}"}
            </Button>
            <Button size="small" onClick={() => insertVariable("telefono")}>
              {"{telefono}"}
            </Button>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            label="Mensaje SMS"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            margin="normal"
            helperText="Puedes usar las variables {nombre} y {telefono}"
          />

          <Box className={classes.segmentInfo}>
            <Typography variant="body2" className={classes.charCounter}>
              Caracteres: {charCount} / 1600
            </Typography>
            <Typography variant="body2" className={classes.charCounter}>
              Segmentos SMS: {segments} {segments > 1 ? `(${segments} mensajes)` : ""}
            </Typography>
            {segments > 10 && (
              <Typography variant="caption" color="error">
                Advertencia: El mensaje es muy largo y se dividirá en {segments} SMS
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateCampaign}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Crear Campaña
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalles de la Campaña</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedCampaign.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Estado: {getStatusLabel(selectedCampaign.status)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Mensaje</Typography>
                <Typography variant="body2">{selectedCampaign.message}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Estadísticas</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Total de Destinatarios</TableCell>
                      <TableCell align="right">{selectedCampaign.totalRecipients}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SMS Enviados</TableCell>
                      <TableCell align="right">{selectedCampaign.sentCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SMS Fallidos</TableCell>
                      <TableCell align="right">{selectedCampaign.failedCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Pendientes</TableCell>
                      <TableCell align="right">{selectedCampaign.pendingCount}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipients Management Dialog */}
      <Dialog
        open={recipientsDialogOpen}
        onClose={() => setRecipientsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Gestionar Destinatarios</DialogTitle>
        <DialogContent>
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
            <Tab label="Agregar Manual" />
            <Tab label="Importar CSV" />
            <Tab label="Lista" />
          </Tabs>

          {currentTab === 0 && (
            <Box className={classes.tabContent}>
              <TextField
                fullWidth
                variant="outlined"
                label="Número de Teléfono *"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                margin="normal"
                helperText="Formato: +52XXXXXXXXXX o XXXXXXXXXX"
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddRecipient}
                style={{ marginTop: 16 }}
              >
                Agregar Destinatario
              </Button>
            </Box>
          )}

          {currentTab === 1 && (
            <Box className={classes.tabContent}>
              <Typography variant="body2" paragraph>
                El archivo CSV debe contener las columnas: telefono (obligatorio), nombre
              </Typography>
              <Typography variant="body2" paragraph color="textSecondary">
                Ejemplo: telefono,nombre<br />
                +5215512345678,Juan Pérez<br />
                5512345679,María García
              </Typography>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                style={{ marginBottom: 16 }}
              />
              <br />
              <Button
                variant="contained"
                color="primary"
                onClick={handleImportCSV}
                startIcon={<CloudUploadIcon />}
                disabled={!csvFile}
              >
                Importar CSV
              </Button>
            </Box>
          )}

          {currentTab === 2 && (
            <Box className={classes.tabContent}>
              <Typography variant="body2" paragraph>
                Total: {recipients.length} destinatarios
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients.map((recipient) => (
                    <TableRow key={recipient.id}>
                      <TableCell>{recipient.phoneNumber}</TableCell>
                      <TableCell>{recipient.name || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={recipient.status || "pending"}
                          size="small"
                          color={recipient.status === "sent" ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRecipient(recipient.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientsDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Reporte de Campaña SMS</DialogTitle>
        <DialogContent>
          {campaignReport && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper style={{ padding: 16, backgroundColor: "#f5f5f5" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Total
                      </Typography>
                      <Typography variant="h5">
                        {campaignReport.totalRecipients}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Enviados
                      </Typography>
                      <Typography variant="h5" style={{ color: "#4caf50" }}>
                        {campaignReport.sentCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Fallidos
                      </Typography>
                      <Typography variant="h5" style={{ color: "#f44336" }}>
                        {campaignReport.failedCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Progreso
                      </Typography>
                      <Typography variant="h5">
                        {campaignReport.progress}%
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default SmsExpress;
